import type { ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, Inbox, Loader2 } from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";

import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button/Button";
import { DataTable } from "@/shared/ui/data-table/DataTable";
import { Input } from "@/shared/ui/input/Input";
import { Label } from "@/shared/ui/label/Label";

import {
  useRunSearchActionMutation,
  type ActionDescriptor,
  type SearchHit,
} from "../api/searchApi";
import {
  buildAction,
  describeError,
  formatCell,
  formatMonth,
  humanize,
  isIdColumn,
  pickRows,
  type PanelState,
} from "../lib/actions";

type ResultRowData = Record<string, unknown>;

const nowYm = (): string => {
  const dt = new Date();
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

interface ActionPanelProps {
  hit: SearchHit;
  d: ActionDescriptor;
  onBack: () => void;
  onNavigate: (to: string) => void;
}

/** Runs an API action (asking for a month first when required) and shows the result. */
export const ActionPanel = memo(function ActionPanel({
  hit,
  d,
  onBack,
  onNavigate,
}: ActionPanelProps) {
  const [month, setMonth] = useState<string>(nowYm);
  const [runAction, { data: result, error, isLoading }] = useRunSearchActionMutation();

  const run = useMemo(
    () => async () => {
      const built = buildAction(
        hit,
        d,
        d.requiresMonth ? formatMonth(month, d.monthFormat) : undefined
      );
      if ("navigateTo" in built) {
        if (built.navigateTo) onNavigate(built.navigateTo);
        return;
      }
      await runAction(built);
    },
    [hit, d, month, onNavigate, runAction]
  );

  // Auto-run when no month is required.
  useEffect(() => {
    if (!d.requiresMonth) void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const panelError: PanelState | null = error
    ? describeError(
        typeof (error as { status?: unknown }).status === "number"
          ? (error as { status: number }).status
          : 0,
        d.label
      )
    : null;

  const rows = useMemo(() => pickRows(result), [result]);
  const cols = useMemo(() => {
    const first = rows[0];
    return first
      ? Object.keys(first)
          .filter((k) => typeof first[k] !== "object")
          .slice(0, 6)
      : [];
  }, [rows]);

  const columns = useMemo<ColumnDef<ResultRowData, unknown>[]>(
    () =>
      cols.map((c) => ({
        accessorKey: c,
        header: humanize(c),
        cell: ({ getValue }) => {
          const value = formatCell(getValue());
          return (
            <span
              title={value}
              className={cn(
                "block max-w-[220px] truncate",
                isIdColumn(c) ? "font-mono text-[11px] text-muted-foreground" : ""
              )}
            >
              {value}
            </span>
          );
        },
      })),
    [cols]
  );

  return (
    <div className="flex flex-col max-h-[80vh]">
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-border">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label="Back"
          className="h-8 w-8 shrink-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground truncate">
            {d.label} · {hit.name}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {hit.hierarchyPath || hit.level}
          </div>
        </div>
        {!isLoading && rows.length > 0 && (
          <span className="shrink-0 rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-medium text-primary-600">
            {rows.length} {rows.length === 1 ? "record" : "records"}
          </span>
        )}
      </div>

      {d.requiresMonth && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
          <Label htmlFor="global-search-month" className="text-xs text-muted-foreground">
            Month
          </Label>
          <Input
            id="global-search-month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-8 w-auto text-sm"
          />
          <Button type="button" size="sm" onClick={() => void run()} disabled={isLoading}>
            Run
          </Button>
        </div>
      )}

      <div className="flex-1 overflow-auto p-4 min-h-[340px]">
        {isLoading ? (
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : panelError ? (
          <div className="flex h-[300px] flex-col items-center justify-center gap-3 px-6 text-center">
            <span
              className={cn(
                "flex h-14 w-14 items-center justify-center rounded-full",
                panelError.tone === "danger"
                  ? "bg-destructive/10 text-destructive"
                  : "bg-muted text-muted-foreground"
              )}
            >
              <panelError.icon className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-[15px] font-semibold text-foreground">{panelError.title}</p>
              <p className="max-w-sm text-sm text-muted-foreground">{panelError.detail}</p>
            </div>
          </div>
        ) : rows.length ? (
          <DataTable<ResultRowData, unknown>
            columns={columns}
            data={rows.slice(0, 100)}
            enableSorting={false}
          />
        ) : result ? (
          <div className="flex h-[300px] flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Inbox className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-[15px] font-semibold text-foreground">Nothing to show</p>
              <p className="max-w-sm text-sm text-muted-foreground">
                The request completed but returned no records to display.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});
